<template>
  <div class="payload-zones-map-wrapper">
    <!-- Controls Toolbar Above Map -->
    <div
      class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1"
      style="gap: 10px;"
    >
      <div
        class="d-flex align-items-center flex-wrap"
        style="gap: 8px;"
      >
        <span class="font-small-2 font-weight-bolder text-muted">{{ $t('Display:') }}</span>
        <b-button-group
          size="sm"
          class="flex-wrap"
        >
          <b-button
            :variant="zoneFilter === 'all' ? 'primary' : 'outline-primary'"
            class="d-inline-flex align-items-center mb-25"
            @click="zoneFilter = 'all'"
          >
            <feather-icon
              icon="LayersIcon"
              size="13"
              class="mr-25"
            />
            <span>{{ $t('All yards ({count})', { count: allZonesCount }) }}</span>
          </b-button>
          <b-button
            :variant="zoneFilter === 'load' ? 'primary' : 'outline-primary'"
            class="d-inline-flex align-items-center mb-25"
            @click="zoneFilter = 'load'"
          >
            <span class="bullet-dot bg-info mr-50" />
            <span>{{ $t('Loading ({count})', { count: loadZones.length }) }}</span>
          </b-button>
          <b-button
            :variant="zoneFilter === 'unload' ? 'primary' : 'outline-primary'"
            class="d-inline-flex align-items-center mb-25"
            @click="zoneFilter = 'unload'"
          >
            <span class="bullet-dot bg-warning mr-50" />
            <span>{{ $t('Unloading ({count})', { count: unloadZones.length }) }}</span>
          </b-button>
        </b-button-group>

        <b-button-group
          size="sm"
          class="flex-wrap ml-md-1"
        >
          <b-button
            :variant="mapLayerType === 'osm' ? 'secondary' : 'outline-secondary'"
            class="d-inline-flex align-items-center mb-25"
            @click="mapLayerType = 'osm'"
          >
            <feather-icon
              icon="MapIcon"
              size="13"
              class="mr-25"
            />
            <span>{{ $t('Standard') }}</span>
          </b-button>
          <b-button
            :variant="mapLayerType === 'satellite' ? 'secondary' : 'outline-secondary'"
            class="d-inline-flex align-items-center mb-25"
            @click="mapLayerType = 'satellite'"
          >
            <feather-icon
              icon="ImageIcon"
              size="13"
              class="mr-25"
            />
            <span>{{ $t('Satellite') }}</span>
          </b-button>
        </b-button-group>
      </div>

      <div class="d-flex align-items-center text-muted font-small-2">
        <feather-icon
          icon="InfoIcon"
          size="13"
          class="mr-25 text-primary"
        />
        <span>{{ $t('Operational yards grouped within a 150m radius') }}</span>
        <b-button
          v-b-tooltip.hover
          variant="flat-primary"
          size="sm"
          class="p-0 ml-50 font-small-1 font-weight-bolder text-primary text-decoration-none d-inline-flex align-items-center cursor-pointer"
          :title="$t('Understand how operational yards are calculated and grouped')"
          @click="$emit('open-guide', 4)"
        >
          <feather-icon
            icon="HelpCircleIcon"
            size="12"
            class="mr-25"
          />
          <span>{{ $t('How does it work?') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Map Container or Empty State -->
    <div
      v-if="allZonesCount > 0"
      class="map-container position-relative rounded overflow-hidden border shadow-sm"
    >
      <l-map
        ref="mapRef"
        :zoom="mapZoom"
        :center="mapCenter"
        :options="{ scrollWheelZoom: true }"
        class="payload-zones-leaflet-map"
        style="width: 100%; z-index: 1;"
        @ready="onMapReady"
      >
        <l-tile-layer
          :url="tileLayerUrl"
          :attribution="tileLayerAttribution"
        />

        <!-- Carregamento (Load Zones) -->
        <template v-if="zoneFilter === 'all' || zoneFilter === 'load'">
          <l-layer-group
            v-for="(zone, idx) in loadZones"
            :key="`load-group-${idx}`"
          >
            <!-- 150m Radius Circle -->
            <l-circle
              :lat-lng="[zone.lat, zone.lng]"
              :radius="150"
              :color="'#00cfe8'"
              :fill-color="'#00cfe8'"
              :fill-opacity="0.18"
              :weight="2"
            />

            <!-- Marker Pin -->
            <l-marker
              :lat-lng="[zone.lat, zone.lng]"
              :icon="loadIcon"
            >
              <l-popup>
                <div
                  class="p-50 font-small-2"
                  style="min-width: 200px;"
                >
                  <div class="d-flex align-items-center mb-50 border-bottom pb-25">
                    <span class="bullet-dot bg-info mr-50" />
                    <strong class="text-info font-weight-bolder">{{ $t('Loading Yard') }}</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-25">
                    <span class="text-muted">{{ $t('Cycles Performed:') }}</span>
                    <strong class="text-dark">{{ zone.cycles }}</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-25">
                    <span class="text-muted">{{ $t('Average Load:') }}</span>
                    <strong class="text-primary">{{ formatNumber(zone.averagePayloadTonnes) }} t</strong>
                  </div>
                  <div
                    v-if="zone.optimalPercent !== null"
                    class="d-flex justify-content-between mb-25"
                  >
                    <span class="text-muted">{{ $t('Compliance Index:') }}</span>
                    <strong class="text-success">{{ formatNumber(zone.optimalPercent) }}%</strong>
                  </div>
                  <div class="mt-50 pt-25 border-top text-muted font-small-1">
                    Coord: {{ zone.lat.toFixed(6) }}, {{ zone.lng.toFixed(6) }}
                  </div>
                </div>
              </l-popup>
            </l-marker>
          </l-layer-group>
        </template>

        <!-- Descarregamento (Unload Zones) -->
        <template v-if="zoneFilter === 'all' || zoneFilter === 'unload'">
          <l-layer-group
            v-for="(zone, idx) in unloadZones"
            :key="`unload-group-${idx}`"
          >
            <!-- 150m Radius Circle -->
            <l-circle
              :lat-lng="[zone.lat, zone.lng]"
              :radius="150"
              :color="'#ff9f43'"
              :fill-color="'#ff9f43'"
              :fill-opacity="0.18"
              :weight="2"
            />

            <!-- Marker Pin -->
            <l-marker
              :lat-lng="[zone.lat, zone.lng]"
              :icon="unloadIcon"
            >
              <l-popup>
                <div
                  class="p-50 font-small-2"
                  style="min-width: 200px;"
                >
                  <div class="d-flex align-items-center mb-50 border-bottom pb-25">
                    <span class="bullet-dot bg-warning mr-50" />
                    <strong class="text-warning font-weight-bolder">{{ $t('Unloading Yard') }}</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-25">
                    <span class="text-muted">{{ $t('Cycles Performed:') }}</span>
                    <strong class="text-dark">{{ zone.cycles }}</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-25">
                    <span class="text-muted">{{ $t('Average Load:') }}</span>
                    <strong class="text-primary">{{ formatNumber(zone.averagePayloadTonnes) }} t</strong>
                  </div>
                  <div
                    v-if="zone.optimalPercent !== null"
                    class="d-flex justify-content-between mb-25"
                  >
                    <span class="text-muted">{{ $t('Compliance Index:') }}</span>
                    <strong class="text-success">{{ formatNumber(zone.optimalPercent) }}%</strong>
                  </div>
                  <div class="mt-50 pt-25 border-top text-muted font-small-1">
                    Coord: {{ zone.lat.toFixed(6) }}, {{ zone.lng.toFixed(6) }}
                  </div>
                </div>
              </l-popup>
            </l-marker>
          </l-layer-group>
        </template>
      </l-map>

      <!-- Recenter Floating Button -->
      <div
        class="position-absolute fit-bounds-floating-wrapper"
        style="top: 14px; right: 14px; z-index: 999;"
      >
        <b-button
          v-b-tooltip.hover
          variant="primary"
          size="sm"
          class="fit-bounds-floating-btn d-inline-flex align-items-center shadow-lg"
          :title="$t('Adjust zoom to view all operational yards on the map')"
          @click="fitMapToBounds"
        >
          <feather-icon
            icon="Maximize2Icon"
            size="15"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Adjust Zoom (All Yards)') }}</span>
          <span class="d-inline d-sm-none">{{ $t('Adjust Zoom') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Empty State if no coordinates or empty zones -->
    <div
      v-else
      class="text-center py-4 border rounded bg-light-secondary"
    >
      <b-avatar
        variant="light-primary"
        size="56px"
        class="mb-1"
      >
        <feather-icon
          icon="MapPinIcon"
          size="28"
          class="text-primary"
        />
      </b-avatar>
      <h6 class="font-weight-bolder mb-25 text-dark">
        {{ $t('No geographic yard identified') }}
      </h6>
      <p
        class="text-muted font-small-3 mb-0 mx-auto"
        style="max-width: 480px;"
      >
        {{ $t('The operational loading and unloading yards are automatically generated using valid GPS coordinates grouped within a 150m radius. In the selected period there was not enough valid geolocation data to form yards.') }}
      </p>
      <b-button
        variant="outline-primary"
        size="sm"
        class="mt-1 d-inline-flex align-items-center"
        @click="$emit('open-guide', 4)"
      >
        <feather-icon
          icon="HelpCircleIcon"
          size="14"
          class="mr-50"
        />
        <span>{{ $t('How are operational yards calculated?') }}</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  ref, computed, watch, onMounted,
} from '@vue/composition-api'
import {
  BButtonGroup,
  BButton,
  BAvatar,
  VBTooltip,
} from 'bootstrap-vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LCircle,
  LLayerGroup,
} from 'vue2-leaflet'
import { getIntlLocale } from '@/libs/i18n/dateLocale'

export default {
  name: 'PayloadZonesMap',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BButtonGroup,
    BButton,
    BAvatar,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LCircle,
    LLayerGroup,
  },
  props: {
    loadZones: {
      type: Array,
      default: () => [],
    },
    unloadZones: {
      type: Array,
      default: () => [],
    },
    nominalCapacity: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props) {
    const mapRef = ref(null)
    const zoneFilter = ref('all')
    const mapLayerType = ref('satellite')

    const tileLayerUrl = computed(() => {
      if (mapLayerType.value === 'satellite') {
        return 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      }
      return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    })

    const tileLayerAttribution = computed(() => {
      if (mapLayerType.value === 'satellite') {
        return '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      }
      return '&copy; OpenStreetMap contributors'
    })

    const mapZoom = ref(13)
    const mapCenter = ref([-15.7801, -47.9292]) // Default fallback center (Brazil)

    const allZonesCount = computed(() => (
      (props.loadZones ? props.loadZones.length : 0) + (props.unloadZones ? props.unloadZones.length : 0)
    ))

    // Custom modern SVG Pin Icons for Leaflet
    const loadIcon = L.divIcon({
      className: 'custom-map-pin-load',
      html: `
        <div style="background-color: #00cfe8; width: 28px; height: 28px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
          <div style="transform: rotate(45deg); width: 8px; height: 8px; background-color: #ffffff; border-radius: 50%;"></div>
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28],
    })

    const unloadIcon = L.divIcon({
      className: 'custom-map-pin-unload',
      html: `
        <div style="background-color: #ff9f43; width: 28px; height: 28px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
          <div style="transform: rotate(45deg); width: 8px; height: 8px; background-color: #ffffff; border-radius: 50%;"></div>
        </div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28],
    })

    const formatNumber = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '—'
      return Number(val).toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })
    }

    const fitMapToBounds = () => {
      if (!mapRef.value || !mapRef.value.mapObject) return
      const map = mapRef.value.mapObject

      const points = []
      if (zoneFilter.value === 'all' || zoneFilter.value === 'load') {
        props.loadZones.forEach(z => {
          if (z.lat && z.lng) points.push([z.lat, z.lng])
        })
      }
      if (zoneFilter.value === 'all' || zoneFilter.value === 'unload') {
        props.unloadZones.forEach(z => {
          if (z.lat && z.lng) points.push([z.lat, z.lng])
        })
      }

      if (points.length > 0) {
        const bounds = L.latLngBounds(points)
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16, animate: true })
      }
    }

    const onMapReady = () => {
      setTimeout(() => {
        if (mapRef.value && mapRef.value.mapObject) {
          mapRef.value.mapObject.invalidateSize()
          fitMapToBounds()
        }
      }, 250)
    }

    watch(
      () => [props.loadZones, props.unloadZones, zoneFilter.value],
      () => {
        setTimeout(fitMapToBounds, 100)
      },
    )

    onMounted(() => {
      setTimeout(fitMapToBounds, 300)
    })

    return {
      mapRef,
      zoneFilter,
      mapLayerType,
      tileLayerUrl,
      tileLayerAttribution,
      mapZoom,
      mapCenter,
      allZonesCount,
      loadIcon,
      unloadIcon,
      formatNumber,
      fitMapToBounds,
      onMapReady,
    }
  },
}
</script>

<style scoped>
.bullet-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.payload-zones-leaflet-map {
  height: 480px;
}

.fit-bounds-floating-btn {
  background: linear-gradient(118deg, #7367f0, #9055fd) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 14px 0 rgba(115, 103, 240, 0.5) !important;
  border-radius: 6px !important;
  padding: 0.45rem 0.85rem !important;
  transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  cursor: pointer;
}

.fit-bounds-floating-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px 0 rgba(115, 103, 240, 0.75) !important;
  filter: brightness(1.06);
}

.fit-bounds-floating-btn:active {
  transform: translateY(0);
}

@media (max-width: 767.98px) {
  .payload-zones-leaflet-map {
    height: 360px;
  }
}
</style>
